import { CameraIcon, ImageUpIcon } from 'lucide-react'
import { type ChangeEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CameraCaptureModal } from '@/components/molecules/camera-capture-modal'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type ImageUploadControlsProps = {
  /** Receives the chosen/captured files; the parent owns the actual upload pipeline. */
  onUpload(files: File[]): void | Promise<void>
  disabled?: boolean
  /** When false, only one image is picked; extra camera shots are dropped (last one wins). */
  multiple?: boolean
  className?: string
}

const ImageUploadControls = ({
  onUpload,
  disabled,
  multiple = true,
  className,
}: ImageUploadControlsProps) => {
  const { t } = useTranslation()
  const [cameraOpen, setCameraOpen] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const runUpload = async (picked: File[]) => {
    const files = multiple ? picked : picked.slice(-1)
    if (files.length === 0) return
    setIsUploading(true)
    try {
      await onUpload(files)
    } finally {
      setIsUploading(false)
    }
  }

  const handleFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
    // Some browsers report HEIC/HEIF with an empty MIME type; let the caller decide on those.
    const files = Array.from(event.target.files ?? []).filter(
      f => f.type.startsWith('image/') || /\.hei[cf]$/i.test(f.name),
    )
    event.target.value = ''
    void runUpload(files)
  }

  const busy = disabled || isUploading

  return (
    <div className={cn('flex gap-2', className)}>
      <Button
        type="button"
        variant="outline"
        disabled={busy}
        onClick={() => fileInputRef.current?.click()}
      >
        <ImageUpIcon className="size-4" />
        {t('imageUpload.uploadImage')}
      </Button>
      <Button type="button" variant="outline" disabled={busy} onClick={() => setCameraOpen(true)}>
        <CameraIcon className="size-4" />
        {t('imageUpload.takePhoto')}
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={handleFilesPicked}
      />
      <CameraCaptureModal
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        onCapture={files => void runUpload(files)}
      />
    </div>
  )
}

export default ImageUploadControls
